/**
 * FlahaSOIL v2 — ReportCoverMetadata (Phase 10D.1).
 *
 * Structured metadata grid on the cover page.
 * Uses "Not provided" / "Not tested" / "Unavailable" for missing values
 * as mandated by the Phase 10D.1 spec.
 */
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import type { ReportCoverSection } from "@flaha/shared-types";
import { flahaSoilColors } from "../../../../theme/flahaSoilTheme";

interface MetaFieldProps {
  label: string;
  value?: string | number | null | undefined;
  fallback?: string;
}

function MetaField({ label, value, fallback = "Not provided" }: MetaFieldProps) {
  const display =
    value !== null && value !== undefined && value !== "" ? String(value) : fallback;
  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: flahaSoilColors.textSecondary, fontSize: "0.65rem", letterSpacing: "0.12em" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: flahaSoilColors.textPrimary }}>
        {display}
      </Typography>
    </Box>
  );
}

export interface ReportCoverMetadataProps {
  cover: ReportCoverSection;
  versionNumber?: number | undefined;
}

export function ReportCoverMetadata({ cover, versionNumber }: ReportCoverMetadataProps) {
  const coords =
    cover.latitude != null && cover.longitude != null
      ? `${cover.latitude.toFixed(4)}°, ${cover.longitude.toFixed(4)}°`
      : null;

  const dateText = cover.reportDate
    ? new Date(cover.reportDate).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Not provided";

  const versionLabel =
    cover.reportVersionLabel ??
    (versionNumber !== undefined
      ? `V${String(versionNumber).padStart(2, "0")}`
      : cover.versionNumber != null
        ? `V${String(cover.versionNumber).padStart(2, "0")}`
        : null);
  const versionWithSupersede =
    versionLabel && cover.supersedesVersion != null
      ? `${versionLabel} (supersedes V${String(cover.supersedesVersion).padStart(2, "0")})`
      : versionLabel;
  const evidence =
    cover.evidenceCompletenessPercent != null
      ? `${cover.evidenceCompletenessStatus ?? "—"} · ${cover.evidenceCompletenessPercent}%`
      : null;

  return (
    <Box sx={{ px: 5, py: 4, backgroundColor: flahaSoilColors.analyticalCream, flex: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2.5}>
            <MetaField label="Farm / Project" value={cover.projectName} />
            <MetaField
              label="Project ref"
              value={cover.projectDisplayRef ?? cover.projectCode}
            />
            <MetaField label="Client" value={cover.clientName} />
            <MetaField label="Client reference" value={cover.clientReference} />
            <MetaField label="Purchase order" value={cover.purchaseOrder} />
            <MetaField label="Location" value={cover.location} />
            <MetaField label="Coordinates" value={coords} />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={2.5}>
            <MetaField
              label="Sample ref"
              value={cover.sampleDisplayRef ?? cover.sampleCode ?? cover.sampleId}
            />
            <MetaField label="Client sample ID" value={cover.clientSampleId} />
            <MetaField label="Soil test ref" value={cover.soilTestDisplayRef} />
            <MetaField label="Report Date" value={dateText} fallback="Not provided" />
            <MetaField
              label="Report Number"
              value={cover.reportNumber}
              fallback="Unavailable"
            />
            <MetaField label="Version" value={versionWithSupersede} />
            <MetaField
              label="Declared level"
              value={cover.declaredTestLevel ?? cover.testLevel}
              fallback="Not tested"
            />
            <MetaField label="Evidence completeness" value={evidence} />
            <MetaField label="Test matrix" value={cover.testMatrixVersion} />
            <MetaField label="Lab reference" value={cover.labReference} />
          </Stack>
        </Grid>
      </Grid>

      {cover.consultantName && (
        <>
          <Divider sx={{ my: 3, borderColor: flahaSoilColors.divider }} />
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <MetaField
              label="Consultant"
              value={
                cover.consultantRole
                  ? `${cover.consultantName} · ${cover.consultantRole}`
                  : cover.consultantName
              }
            />
          </Stack>
        </>
      )}
    </Box>
  );
}
